/* The read-only half of the domain rules, so a tile can be drawn without a
   round trip. The server holds the authority (server/src/lib/domain.js) and
   re-derives all of this on write; nothing here decides what is stored. */

export const CADENCE = {
  weekly: { label: "Weekly", atRisk: 7, critical: 14 },
  monthly: { label: "Monthly", atRisk: 30, critical: 45 },
};

export const HEALTH = {
  healthy: { label: "Healthy", tone: "tone-ok" },
  "at-risk": { label: "At risk", tone: "tone-warn" },
  critical: { label: "Critical", tone: "tone-bad" },
};

export const TASK_STATUS = {
  todo: { label: "To do", cls: "" },
  "in-progress": { label: "In progress", cls: "prog" },
  blocked: { label: "Blocked", cls: "blocked" },
  done: { label: "Done", cls: "done" },
};

export const PLAYER_STATUS = {
  active: "Active",
  overloaded: "Overloaded",
  "on-leave": "On leave",
};

export const pad2 = (n) => String(n).padStart(2, "0");

export const ymd = (d) =>
  d.getFullYear() + "-" + pad2(d.getMonth() + 1) + "-" + pad2(d.getDate());

export function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const jan1 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t - jan1) / 86400000 + 1) / 7);
  return t.getUTCFullYear() + "-W" + pad2(week);
}

export function daysSince(iso) {
  const t = Date.parse(iso);
  if (isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

export const cadenceOf = (pg) => CADENCE[pg.cadence] || CADENCE.weekly;

export function computedHealth(pg) {
  const days = pg.lastMeetingAt ? daysSince(pg.lastMeetingAt) : null;
  if (days === null) return "critical";
  const c = cadenceOf(pg);
  if (days < c.atRisk) return "healthy";
  if (days < c.critical) return "at-risk";
  return "critical";
}

export const healthOf = (pg) => pg.manualHealth || computedHealth(pg);

export const openTasks = (pg) => pg.tasks.filter((t) => t.status !== "done").length;

export function isOverdue(task) {
  return Boolean(task.due) && task.status !== "done" && task.due < ymd(new Date());
}

export function dueSoon(task) {
  if (!task.due || task.status === "done") return false;
  const today = ymd(new Date());
  const horizon = new Date();
  horizon.setDate(horizon.getDate() + 2);
  return task.due >= today && task.due <= ymd(horizon);
}

export const kpiDue = (k) => Boolean(k.reviewOn) && k.reviewOn <= ymd(new Date());

export function clockTime(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return pad2(d.getHours()) + ":" + pad2(d.getMinutes());
}

export function sinceLabel(iso) {
  const d = daysSince(iso);
  if (d === null) return "";
  if (d === 0) return "notified today";
  if (d === 1) return "notified yesterday";
  return "notified " + d + "d ago";
}

/* The last n weekdays, oldest first — the accountability grid's columns. */
export function lastWeekdays(n) {
  const out = [];
  const d = new Date();
  while (out.length < n) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(ymd(d));
    d.setDate(d.getDate() - 1);
  }
  return out.reverse();
}

/* A YYYY-MM-DD back to a local date. `new Date("2026-03-12")` reads the
   string as UTC and so lands on the day before for anyone west of
   Greenwich; the grid is keyed by local calendar days, so it is built from
   the parts. */
export function parseYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s || ""));
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/* Every weekday from one date to another, inclusive, oldest first.
 *
 * This is what "since they joined" is made of: a window whose length nobody
 * knows in advance, rather than a fixed count backwards from today.
 * Weekends are skipped for the same reason lastWeekdays skips them — nobody
 * is expected to work the board on a Saturday, so a Saturday is not a miss.
 *
 * Capped, because the window's start comes from a stored date and a wrong
 * one must not turn into an unbounded loop. */
export function weekdaysBetween(from, to, cap = 2000) {
  const start = parseYmd(from);
  const end = parseYmd(to);
  if (!start || !end || start > end) return [];
  const out = [];
  const d = start;
  while (d <= end && out.length < cap) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(ymd(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

/* Saturday and Sunday are not working days here. Nothing is expected on
   one, so nothing is counted against anyone for one: the grid leaves them
   out of its columns entirely, and the acknowledgement bar stands down
   rather than asking for something no record will ever read. */
export function isWeekend(iso) {
  const d = parseYmd(iso);
  if (!d) return false;
  const g = d.getDay();
  return g === 0 || g === 6;
}

/* The weekday a date falls on, as the grid labels it. */
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function dayName(iso) {
  const d = parseYmd(iso);
  return d ? DAY_NAMES[d.getDay()] : "";
}

/* "12 March 2026" — the long form, for a list somebody reads rather than
   scans. The grid itself stays on YYYY-MM-DD. */
export function longDate(iso) {
  const d = parseYmd(iso);
  if (!d) return iso;
  return d.getDate() + " " + MONTH_NAMES[d.getMonth()] + " " + d.getFullYear();
}

/* "March 2026" — the heading a run of days is grouped under. */
export function monthLabel(iso) {
  const d = parseYmd(iso);
  if (!d) return "";
  return MONTH_NAMES[d.getMonth()] + " " + d.getFullYear();
}

/* "12 Mar" — the short form the row uses to say where a record starts. */
export function shortDate(iso) {
  const d = parseYmd(iso);
  if (!d) return "";
  return d.getDate() + " " + MONTH_NAMES[d.getMonth()].slice(0, 3);
}
